import { fmt2 } from "@/lib/sqaafLabels";
import { BAND_COLORS } from "@/components/sqaaf/sqaafConstants";

const BAND_ORDER = ["Critical", "Developing", "Strong", "Exemplary", "Needs Focus"];

const DONUT_COLORS = {
  Critical: "#ef4444",
  Developing: "#f59e0b",
  Strong: "#22c55e",
  Exemplary: "#3b82f6",
  "Needs Focus": "#ff8a1f",
};

const BAND_THRESHOLDS = [
  { max: 40, label: "Critical" },
  { max: 55, label: "Needs Focus" },
  { max: 70, label: "Developing" },
  { max: 85, label: "Strong" },
  { max: Infinity, label: "Exemplary" },
];

export function getDistrictBandLabel(mean) {
  const score = Number(mean);
  if (!Number.isFinite(score)) return "Developing";
  for (const t of BAND_THRESHOLDS) {
    if (score <= t.max) return t.label;
  }
  return "Exemplary";
}

export function getDistrictBandCopy(band) {
  const map = {
    Critical: "District quality is in critical band",
    "Needs Focus": "District quality needs focused improvement",
    Developing: "District quality is in developing band",
    Strong: "District quality is in strong band",
    Exemplary: "District quality is in exemplary band",
  };
  return map[band] || map.Developing;
}

export function buildExecutiveHeroBody(overview = {}) {
  const n = Number(overview.n_schools || 0).toLocaleString();
  const completion = fmt2(overview.completion_rate);
  const developingPct = fmt2(overview.developing_pct);
  const criticalPct = fmt2(overview.critical_pct);
  const equityGap = fmt2(overview.equity_gap);
  const interventionLoad = fmt2(overview.intervention_load);
  return `${completion}% average answer completion across ${n} schools. ${developingPct}% are in the developing band and ${criticalPct}% are critical. Focus on the ${equityGap}-point equity gap and ${interventionLoad}% intervention load (schools below 60%).`;
}

export function buildExecutiveActionSignal(overview = {}, blockPerf = {}, intervention = {}) {
  const below60 = Number(overview.below_60_count ?? intervention?.intervention_summary?.below_60_count ?? 0);
  const blocks = blockPerf?.block_summary || [];
  const bottomBlock = [...blocks].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0))[0];
  const lowestQ = (intervention?.bottom_questions || [])[0];
  let signal = `Prioritize ${below60.toLocaleString()} schools below 60%`;
  if (bottomBlock) {
    signal += `, starting with ${bottomBlock.block} (mean ${fmt2(bottomBlock.mean)}%, ${fmt2(bottomBlock.critical_pct)}% critical)`;
  }
  if (lowestQ) {
    signal += ` and section ${lowestQ.section || "—"} questions averaging ${fmt2(lowestQ["mean_score_%"])}%`;
  }
  return `${signal}.`;
}

export function buildGaugeStyle(scorePct) {
  const pct = Math.min(100, Math.max(0, Number(scorePct) || 0));
  const band = getDistrictBandLabel(pct);
  const color =
    band === "Critical"
      ? "#ef4444"
      : band === "Needs Focus" || band === "Developing"
        ? "#f59e0b"
        : band === "Strong"
          ? "#22c55e"
          : "#3b82f6";
  return {
    background: `radial-gradient(circle at center, #fff 0 53%, transparent 54%), conic-gradient(${color} 0 ${pct}%, rgba(245,158,11,.14) ${pct}% 100%)`,
    color,
  };
}

export function buildDonutGradient(bandDistribution = []) {
  let cumulative = 0;
  const stops = [];
  for (const band of BAND_ORDER) {
    const item = bandDistribution.find((b) => b.band === band);
    const pct = Number(item?.pct) || 0;
    const color = DONUT_COLORS[band] || BAND_COLORS[band] || "#94a3b8";
    stops.push(`${color} ${cumulative}% ${cumulative + pct}%`);
    cumulative += pct;
  }
  if (!stops.length) return "conic-gradient(#e2e8f0 0 100%)";
  return `conic-gradient(${stops.join(", ")})`;
}

export function buildInsightCards(overview = {}, criticalCount = 0) {
  const developingCount = Number(overview.band_counts?.Developing ?? 0);
  const strongPlus = fmt2(overview.strong_plus_exemplary_pct ?? 0);
  return [
    {
      tone: "red",
      title: "Critical Band Requires Direct Follow-Up",
      body: `${Number(criticalCount || 0).toLocaleString()} schools are at or below 40% (${fmt2(overview.critical_pct)}% of district), requiring block-wise ownership and weekly closure tracking.`,
    },
    {
      tone: "orange",
      title: "Equity Gap Is Visible",
      body: `The ${fmt2(overview.equity_gap)}-point equity gap (top 25% minus bottom 25%) and ${developingCount.toLocaleString()} developing-band schools indicate differentiated support is needed.`,
    },
    {
      tone: "green",
      title: "Coverage Is Strong",
      body: `${fmt2(overview.completion_rate)}% average answer completion and ${fmt2(overview.full_compliance_rate)}% full compliance (${Number(overview.full_compliance_count || 0).toLocaleString()} schools) enable quality-focused action. ${strongPlus}% are strong or exemplary.`,
    },
  ];
}

function blockWatchlistNote(block, kind, rank, districtMean = 0) {
  const mean = Number(block.mean_score ?? block.mean ?? 0);
  const criticalPct = fmt2(block.critical_pct ?? 0);
  const schools = Number(block.school_count ?? block.schools ?? 0);
  if (kind === "top") {
    return `Rank #${rank} · ${fmt2(mean)}% mean · ${criticalPct}% critical · ${schools} schools`;
  }
  const gap = fmt2(Math.max(0, districtMean - mean));
  return `${gap} pts below district mean · ${fmt2(block.pct_below_60 ?? 0)}% below 60% · ${schools} schools`;
}

export function buildWatchlistRows(overview = {}, blockPerf = {}) {
  const districtMean = Number(overview.district_mean ?? 0);
  const blockMap = new Map(
    (blockPerf?.block_summary || []).map((b) => [b.block, b])
  );
  const enrichBlock = (b) => {
    const full = blockMap.get(b.block) || {};
    return { ...full, ...b, mean: b.mean ?? b.mean_score ?? full.mean, schools: b.school_count ?? b.schools ?? full.schools };
  };
  const top = (overview.top5_blocks || []).slice(0, 3).map(enrichBlock);
  const bottom = (overview.bottom5_blocks || blockPerf?.block_summary || [])
    .slice()
    .map(enrichBlock)
    .sort((a, b) => (a.mean ?? a.mean_score ?? 0) - (b.mean ?? b.mean_score ?? 0))
    .slice(0, 2);

  const rows = [];
  top.forEach((b, i) => {
    rows.push({
      kind: "top",
      rank: String(i + 1),
      block: b.block,
      score: b.mean_score ?? b.mean,
      note: blockWatchlistNote(b, "top", i + 1, districtMean),
    });
  });
  bottom.forEach((b) => {
    rows.push({
      kind: "low",
      rank: "!",
      block: b.block,
      score: b.mean_score ?? b.mean,
      note: blockWatchlistNote(b, "low", 0, districtMean),
    });
  });
  return rows;
}

export function buildPolicyActions(overview = {}, criticalCount = 0, intervention = {}) {
  const developingCount = Number(overview.band_counts?.Developing ?? 0);
  const below60 = Number(overview.below_60_count ?? intervention?.intervention_summary?.below_60_count ?? 0);
  const incompletePct = fmt2(overview.incomplete_assessment_pct ?? 0);
  const topBlock = [...(intervention?.block_intervention_index || [])].sort(
    (a, b) => (b.intervention_index ?? 0) - (a.intervention_index ?? 0)
  )[0];

  return [
    {
      tone: "red",
      priority: "High",
      priorityClass: "",
      title: "Critical School Remediation",
      body: `Assign ownership for ${Number(criticalCount || 0).toLocaleString()} schools below 40% (${fmt2(overview.critical_pct)}% of district).`,
    },
    {
      tone: "orange",
      priority: "Medium",
      priorityClass: "amber",
      title: "Developing Band Uplift Sprint",
      body: `Target ${developingCount.toLocaleString()} developing-band schools and ${below60.toLocaleString()} sub-60% schools (${fmt2(overview.intervention_load)}% intervention load).`,
    },
    {
      tone: "blue",
      priority: "Routine",
      priorityClass: "blue",
      title: topBlock ? `${topBlock.block} Intervention Review` : "Block Intervention Review",
      body: topBlock
        ? `Highest intervention index ${fmt2(topBlock.intervention_index)} (${fmt2(topBlock.critical_pct)}% critical, ${fmt2(topBlock.pct_below_60)}% below 60%). ${incompletePct}% of schools have incomplete assessments.`
        : `${incompletePct}% of schools have incomplete assessments (<90% answered).`,
    },
  ];
}

export function buildBlockComparisonData(overview = {}, blockPerf = {}) {
  const top = (overview.top5_blocks || []).map((b) => ({
    block: b.block,
    score: Number(b.mean_score ?? b.mean ?? 0),
    tone: "green",
  }));
  const bottomSource = overview.bottom5_blocks?.length
    ? overview.bottom5_blocks
    : [...(blockPerf?.block_summary || [])].sort((a, b) => a.mean - b.mean).slice(0, 5);
  const bottom = bottomSource.map((b) => ({
    block: b.block,
    score: Number(b.mean_score ?? b.mean ?? 0),
    tone: "red",
  }));
  return [...top, ...bottom];
}

export function deriveQuestionPriority(meanPct) {
  const v = Number(meanPct);
  if (v <= 20) return { label: "Critical", tag: "red" };
  if (v <= 40) return { label: "Needs Focus", tag: "amber" };
  return { label: "Track", tag: "blue" };
}

export function deriveQuestionAction(q = {}) {
  const coverage = Number(q.coverage_pct) || 0;
  const pctLe1 = Number(q.pct_score_le_1) || 0;
  const meanPct = Number(q["mean_score_%"]) || 0;
  const section = q.section || q.section_label || "—";
  if (coverage > 0 && coverage < 75) {
    return `Improve response coverage (${fmt2(coverage)}% of schools answered)`;
  }
  if (pctLe1 >= 40) {
    return `${fmt2(pctLe1)}% of schools score ≤1 in section ${section} — deploy remedial evidence`;
  }
  if (meanPct < 25) {
    return `District mean ${fmt2(meanPct)}% in section ${section} — escalate block review`;
  }
  return `Section ${section}: target uplift above ${fmt2(meanPct)}% district mean`;
}

export function buildLowestQuestionsTable(questions = []) {
  return (questions || []).slice(0, 10).map((q) => {
    const meanPct = Number(q["mean_score_%"]) || 0;
    const avgOf4 = (meanPct / 100) * 4;
    const priority = deriveQuestionPriority(meanPct);
    const theme = String(q.question || "")
      .replace(/^\d+\.\d+\.\s*\d+\s*/, "")
      .trim();
    return {
      section: q.section_label || q.section || "—",
      theme: theme || q.question || "—",
      avgOf4: fmt2(avgOf4),
      scorePct: fmt2(meanPct),
      action: deriveQuestionAction(q),
      priority: priority.label,
      priorityTag: priority.tag,
      isLow: meanPct < 40,
    };
  });
}

export function buildHistogramBars(histogram = [], chart = { left: 50, bottom: 250, width: 560, height: 210 }) {
  const maxCount = Math.max(...histogram.map((h) => h.count || 0), 1);
  const barWidth = 18;
  const gap = 8;
  return histogram.map((bin, i) => {
    const count = bin.count || 0;
    const height = (count / maxCount) * chart.height;
    return {
      x: chart.left + 8 + i * (barWidth + gap),
      y: chart.bottom - height,
      width: barWidth,
      height: Math.max(count > 0 ? 2 : 0, height),
      count,
      label: bin.bin_start,
      maxCount,
    };
  });
}

export function buildHistogramYTicks(histogram = [], chart = { bottom: 250, height: 210, left: 50 }) {
  const maxCount = Math.max(...histogram.map((h) => h.count || 0), 1);
  const tickCount = 4;
  const ticks = [];
  for (let i = 1; i <= tickCount; i += 1) {
    const value = Math.round((maxCount * i) / tickCount);
    const y = chart.bottom - (chart.height * i) / tickCount;
    ticks.push({ y, value });
  }
  return ticks;
}

export function buildBlockBarLayout(blocks = [], chart = { left: 60, bottom: 250, height: 220, maxScore: 80 }) {
  const barWidth = 38;
  const gap = 17;
  return blocks.map((b, i) => {
    const score = b.score ?? 0;
    const height = (score / chart.maxScore) * chart.height;
    return {
      x: chart.left + 15 + i * (barWidth + gap),
      y: chart.bottom - height,
      width: barWidth,
      height,
      score: fmt2(score),
      block: b.block,
      tone: b.tone,
    };
  });
}
